<template>
  <v-card
    class="conditional-editor rounded-lg pa-4"
    color="blue-grey lighten-3"
    data-testid="conditionalEditor-container"
    outlined
  >
    <v-row
      class="ma-0 pa-0 mb-3"
      no-gutters
      data-testid="conditionalEditor-row-blockTitle"
    >
      <v-col class="d-flex align-center">
        <v-icon class="me-2" color="primary" size="18">
          mdi-eye-outline
        </v-icon>

        <span
          class="subtitle-2 blue-grey--text text--darken-2"
          data-testid="conditionalEditor-title"
        >
          {{ blockTitle }}
        </span>
      </v-col>
    </v-row>

    <div
      v-for="index in blockCount"
      :key="index"
      class="mb-2"
      :data-testid="`conditionalEditor-container-rule-${index - 1}`"
    >
      <v-row
        class="ma-0 pa-0 align-center"
        no-gutters
        :data-testid="`conditionalEditor-row-rule-${index - 1}`"
      >
        <v-col
          class="pe-2"
          :data-testid="`conditionalEditor-column-triggerQuestion-${index - 1}`"
        >
          <p
            class="caption blue-grey--text text--darken-1 mb-1"
            :data-testid="
              `conditionalEditor-label-triggerQuestion-${index - 1}`
            "
          >
            {{ triggerLabel }}
          </p>
          <v-select
            class="white"
            dense
            hide-details
            outlined
            :data-testid="
              `conditionalEditor-select-triggerQuestion-${index - 1}`
            "
            :disabled="disabled"
            :items="triggerItems(index - 1)"
            :menu-props="{ offsetY: true }"
            :value="selectedTriggerItemId(index - 1)"
            @input="handleTriggerChange(index - 1, $event)"
          />
        </v-col>
        <v-col
          class="ps-2"
          :data-testid="`conditionalEditor-column-triggerOption-${index - 1}`"
        >
          <template v-if="isRangeKind(index - 1)">
            <p
              class="caption blue-grey--text text--darken-1 mb-1"
              :data-testid="
                `conditionalEditor-label-conditionRange-${index - 1}`
              "
            >
              {{ $t('checklist.visibility_rule.condition_range_label') }}
            </p>
            <v-row class="ma-0 pa-0" no-gutters>
              <v-col class="pe-2" cols="5">
                <v-text-field
                  v-disable-number-scroll
                  class="white"
                  dense
                  hide-details
                  outlined
                  type="number"
                  :aria-label="$t('checklist.min')"
                  :data-testid="`conditionalEditor-input-rangeMin-${index - 1}`"
                  :disabled="disabled"
                  :error="hasRangeError(index - 1)"
                  :prefix="$t('checklist.min')"
                  :value="selectedRange(index - 1).min"
                  @input="handleMinInput(index - 1, $event)"
                />
              </v-col>
              <v-col class="d-flex justify-center align-center" cols="2">
                <span
                  class="caption blue-grey--text text--darken-1"
                  :data-testid="
                    `conditionalEditor-text-rangeConnector-${index - 1}`
                  "
                >
                  {{
                    $t('checklist.visibility_rule.condition_range_connector')
                  }}
                </span>
              </v-col>
              <v-col class="ps-2" cols="5">
                <v-text-field
                  v-disable-number-scroll
                  class="white"
                  dense
                  hide-details
                  outlined
                  type="number"
                  :data-testid="`conditionalEditor-input-rangeMax-${index - 1}`"
                  :disabled="disabled"
                  :error="hasRangeError(index - 1)"
                  :prefix="$t('checklist.max')"
                  :value="selectedRange(index - 1).max"
                  @input="handleMaxInput(index - 1, $event)"
                />
              </v-col>
            </v-row>
            <p
              v-if="hasRangeError(index - 1)"
              class="caption error--text mb-0 mt-1"
              :data-testid="`conditionalEditor-text-rangeError-${index - 1}`"
              role="alert"
            >
              {{ rangeError(index - 1)[0] }}
            </p>
          </template>
          <template v-else-if="isMultiKind(index - 1)">
            <p
              class="caption blue-grey--text text--darken-1 mb-1"
              :data-testid="
                `conditionalEditor-label-triggerOption-${index - 1}`
              "
            >
              {{ $t('checklist.visibility_rule.condition_label_multi') }}
            </p>
            <v-autocomplete
              class="white"
              dense
              hide-details
              multiple
              outlined
              :aria-label="
                $t('checklist.visibility_rule.condition_label_multi')
              "
              :data-testid="
                `conditionalEditor-autocomplete-triggerOption-${index - 1}`
              "
              :disabled="disabled"
              :items="conditionItems(index - 1)"
              :menu-props="{ offsetY: true }"
              :value="selectedMultiOptionIds(index - 1)"
              @input="handleMultiChange(index - 1, $event)"
            />
          </template>

          <template v-else>
            <p
              class="caption blue-grey--text text--darken-1 mb-1"
              :data-testid="
                `conditionalEditor-label-triggerOption-${index - 1}`
              "
            >
              {{ $t('checklist.visibility_rule.condition_label') }}
            </p>
            <v-select
              class="white"
              dense
              hide-details
              outlined
              :data-testid="
                `conditionalEditor-select-triggerOption-${index - 1}`
              "
              :disabled="isConditionSelectDisabled(index - 1)"
              :items="conditionItems(index - 1)"
              :menu-props="{ offsetY: true }"
              :value="selectedOptionId(index - 1)"
              @input="handleOptionChange(index - 1, $event)"
            />
          </template>
        </v-col>
        <v-col
          class="conditional-editor__remove-column d-flex flex-column justify-center align-center mt-4"
          :data-testid="`conditionalEditor-column-removeRule-${index - 1}`"
        >
          <Button
            color="blueGrey"
            icon="mdi-delete-outline"
            plain
            size="small"
            :aria-label="$t('checklist.visibility_rule.remove_condition')"
            :data-testid="`conditionalEditor-button-removeRule-${index - 1}`"
            :disabled="disabled"
            :tooltip="$t('checklist.visibility_rule.remove_condition')"
            @click="handleRemoveRule(index - 1)"
          />
        </v-col>
      </v-row>

      <v-row
        v-if="index < blockCount"
        class="ma-0 pa-0 my-4 align-center"
        no-gutters
        :data-testid="`conditionalEditor-row-rulesConnector-${index - 1}`"
      >
        <v-divider
          :data-testid="
            `conditionalEditor-divider-rulesConnectorLeft-${index - 1}`
          "
        />
        <span
          class="subtitle-2 blue-grey--text text--darken-2 mx-2"
          :data-testid="`conditionalEditor-text-rulesConnector-${index - 1}`"
        >
          {{ $t('checklist.visibility_rule.rules_connector') }}
        </span>
        <v-divider
          :data-testid="
            `conditionalEditor-divider-rulesConnectorRight-${index - 1}`
          "
        />
        <v-col
          class="conditional-editor__remove-column"
          :data-testid="
            `conditionalEditor-column-rulesConnectorSpacer-${index - 1}`
          "
        />
      </v-row>
    </div>

    <Button
      v-if="canAddRule"
      class="mt-1"
      color="primary"
      icon="mdi-plus"
      plain
      size="small"
      data-testid="conditionalEditor-button-addCondition"
      :text="$t('checklist.visibility_rule.add_condition')"
      @click="handleAddRule"
    />
  </v-card>
</template>

<script>
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import Button from '@/components/UI/Button'

import { ITEM_TYPES, MAX_VISIBILITY_RULES } from './constants.js'

// Editor condition controls keyed by trigger type; unknown types fall back
// to the shipped single-option control.
const CONDITION_TYPES = Object.freeze({
  OPTIONS: 'options',
  RANGE: 'range',
  MULTI: 'multi',
})

export default {
  name: 'ConditionalEditor',

  components: {
    Button,
  },

  props: {
    item: { type: Object, required: true },
    eligibleTriggers: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    isSaving: { type: Boolean, default: false },
  },

  data() {
    return {
      // One draft per rendered rule block, aligned by index with the saved
      // `visibilityRules`; untouched blocks hold null and converge on the
      // saved rule. A block beyond the saved rules is a pending new rule.
      drafts: (this.item?.visibilityRules ?? []).map(() => null),
      // The rules this editor last emitted, used to recognize the parent's
      // echo of our own change and keep the drafts across it.
      lastEmittedRules: null,
      lastSeenSavedRules: [],
    }
  },

  computed: {
    blockTitle() {
      if (this.item.type === ITEM_TYPES.SEPARATOR) {
        return this.$t('checklist.visibility_rule.block_title_section')
      }

      return this.$t('checklist.visibility_rule.block_title')
    },

    savedRules() {
      return this.item?.visibilityRules ?? []
    },

    blockCount() {
      return this.drafts.length
    },

    shouldShowAddRule() {
      return (
        this.blockCount < MAX_VISIBILITY_RULES &&
        !_.isEmpty(this.triggerItems(this.blockCount))
      )
    },

    canAddRule() {
      return (
        !this.disabled &&
        this.shouldShowAddRule &&
        this.blockCount <= this.savedRules.length &&
        this.drafts.every((draft, index) => !this.isBlockIncomplete(index))
      )
    },

    triggerLabel() {
      return this.$t('checklist.visibility_rule.trigger_label_typed')
    },
  },

  watch: {
    // Realigns the drafts when the saved rules change outside this editor:
    // the echo of our own emission keeps the drafts, while an external
    // removal (e.g. a trigger question being deleted) resets them so they
    // never point at shifted rules.
    savedRules: {
      immediate: true,
      deep: true,
      handler(newRules) {
        const previousRules = this.lastSeenSavedRules
        this.lastSeenSavedRules = _.cloneDeep(newRules)

        if (_.isEqual(newRules, previousRules)) {
          return
        }

        if (
          this.lastEmittedRules &&
          _.isEqual(newRules, this.lastEmittedRules)
        ) {
          this.lastEmittedRules = null
          // A removal echo can unblock a surviving edit whose duplicate peer
          // is gone — flush it so the payload matches the visible drafts.
          this.emitRulesIfChanged()
          return
        }

        if (newRules.length < previousRules.length) {
          this.drafts = newRules.map(() => null)
          this.seedFirstRuleBlock()
        }
      },
    },

    // The payload is authoritative once a save attempt finishes: converge
    // the drafts on the saved rules so unemitted pending blocks and held
    // edits disappear ("keep what was saved").
    isSaving(value, oldValue) {
      if (oldValue && !value) {
        this.resetDraftsToSavedRules()
      }
    },
  },

  mounted() {
    this.seedFirstRuleBlock()
  },

  methods: {
    // Options already used by other blocks, grouped by trigger entity id. A
    // block's resolved rule — saved or drafted — consumes its criteria so
    // another rule of the same question cannot reuse them.
    getConsumedOptionIdsByTrigger(excludeIndex) {
      const consumedByTrigger = {}

      this.getAcceptedBlockRules().forEach((rule, blockIndex) => {
        if (blockIndex === excludeIndex) {
          return
        }

        const triggerItemId = utils.getEntityId(rule?.triggerItemId)

        if (!triggerItemId || !_.isArray(rule?.optionIds)) {
          return
        }

        consumedByTrigger[triggerItemId] = new Set([
          ...(consumedByTrigger[triggerItemId] ?? []),
          ...rule.optionIds.map(optionId => utils.getEntityId(optionId)),
        ])
      })

      return consumedByTrigger
    },

    getConsumedOptionIds(index) {
      const triggerItemId = utils.getEntityId(this.selectedTriggerItemId(index))

      if (!triggerItemId) {
        return new Set()
      }

      return (
        this.getConsumedOptionIdsByTrigger(index)[triggerItemId] ?? new Set()
      )
    },

    isRangeTrigger(trigger) {
      return (
        trigger?.type === ITEM_TYPES.NUMBER ||
        trigger?.type === ITEM_TYPES.SCALE
      )
    },

    isTriggerExhausted(trigger, consumedByTrigger) {
      if (this.isRangeTrigger(trigger)) {
        return false
      }

      const options = _.isArray(trigger?.options) ? trigger.options : []

      if (_.isEmpty(options)) {
        return false
      }

      const consumed =
        consumedByTrigger[utils.getEntityId(trigger?._id)] ?? new Set()

      return options.every(option =>
        consumed.has(utils.getEntityId(option?._id))
      )
    },

    triggerItems(index) {
      const consumedByTrigger = this.getConsumedOptionIdsByTrigger(index)
      const selfTriggerId = utils.getEntityId(this.selectedTriggerItemId(index))

      return this.eligibleTriggers
        .filter(trigger => {
          if (utils.getEntityId(trigger?._id) === selfTriggerId) {
            return true
          }

          return !this.isTriggerExhausted(trigger, consumedByTrigger)
        })
        .map(trigger => ({
          text: this.getTriggerText(trigger),
          value: utils.getEntityId(trigger?._id),
        }))
    },

    getTriggerText(trigger) {
      const name = trigger?.name?.trim()
      return name || this.$t('checklist.question')
    },

    savedRule(index) {
      return index < this.savedRules.length ? this.savedRules[index] : null
    },

    blockDraft(index) {
      return this.drafts[index] ?? {}
    },

    buildEmptyDraft() {
      return {
        triggerItemId: null,
        optionId: null,
        optionIds: [],
        multiTouched: false,
        min: null,
        max: null,
      }
    },

    ensureDraft(index) {
      if (!this.drafts[index]) {
        this.$set(this.drafts, index, this.buildEmptyDraft())
      }

      return this.drafts[index]
    },

    // A freshly opened editor on an unconditioned item seeds one pending
    // block so the first rule is ready to configure without an extra step.
    seedFirstRuleBlock() {
      if (!_.isEmpty(this.savedRules) || !_.isEmpty(this.drafts)) {
        return
      }

      this.drafts.push(this.buildEmptyDraft())
    },

    // Judges the block's effective (draft-converged) state rather than its
    // resolved rule: an incomplete edit holds the saved rule, so only this
    // view can tell the user's emptied criteria apart from a settled block.
    isBlockIncomplete(index) {
      if (!this.selectedTriggerItemId(index)) {
        return true
      }

      const kind = this.conditionKind(index)

      if (kind === CONDITION_TYPES.RANGE) {
        const { min, max } = this.resolvedRange(index)

        return !_.isNumber(min) || !_.isNumber(max) || this.hasRangeError(index)
      }

      if (kind === CONDITION_TYPES.MULTI) {
        return _.isEmpty(this.selectedMultiOptionIds(index))
      }

      return !this.selectedOptionId(index)
    },

    selectedTriggerItemId(index) {
      return (
        this.blockDraft(index).triggerItemId ??
        this.savedRule(index)?.triggerItemId ??
        null
      )
    },

    selectedTrigger(index) {
      return this.eligibleTriggers.find(
        trigger =>
          utils.getEntityId(trigger?._id) === this.selectedTriggerItemId(index)
      )
    },

    conditionKind(index) {
      const type = this.selectedTrigger(index)?.type

      if (type === ITEM_TYPES.NUMBER || type === ITEM_TYPES.SCALE) {
        return CONDITION_TYPES.RANGE
      }

      if (type === ITEM_TYPES.CHECKBOXES) {
        return CONDITION_TYPES.MULTI
      }

      return CONDITION_TYPES.OPTIONS
    },

    isRangeKind(index) {
      return this.conditionKind(index) === CONDITION_TYPES.RANGE
    },

    isMultiKind(index) {
      return this.conditionKind(index) === CONDITION_TYPES.MULTI
    },

    isConditionSelectDisabled(index) {
      return this.disabled || !this.selectedTriggerItemId(index)
    },

    triggerOptions(index) {
      return this.selectedTrigger(index)?.options ?? []
    },

    conditionItems(index) {
      const consumed = this.getConsumedOptionIds(index)
      const selfSelections = new Set(
        [this.selectedOptionId(index), ...this.selectedMultiOptionIds(index)]
          .filter(optionId => !_.isNil(optionId))
          .map(optionId => utils.getEntityId(optionId))
      )

      return this.triggerOptions(index)
        .filter(option => {
          const optionId = utils.getEntityId(option?._id)

          return !consumed.has(optionId) || selfSelections.has(optionId)
        })
        .map(option => ({
          text: option?.name ?? '',
          value: utils.getEntityId(option?._id),
        }))
    },

    selectedOptionId(index) {
      const draft = this.blockDraft(index)

      // An explicit option edit is authoritative even without a trigger
      // draft — otherwise an option-only change on a saved rule would
      // silently converge back to the saved option.
      if (!_.isNil(draft.optionId)) {
        return draft.optionId
      }

      if (draft.triggerItemId) {
        return null
      }

      return this.savedRule(index)?.optionIds?.[0] ?? null
    },

    selectedMultiOptionIds(index) {
      const draft = this.blockDraft(index)

      if (draft.triggerItemId || draft.multiTouched) {
        return draft.optionIds ?? []
      }

      return this.savedRule(index)?.optionIds ?? []
    },

    selectedRange(index) {
      const draft = this.blockDraft(index)
      const savedRange = draft.triggerItemId
        ? {}
        : this.savedRule(index)?.range ?? {}

      return {
        min: draft.min ?? savedRange.min ?? null,
        max: draft.max ?? savedRange.max ?? null,
      }
    },

    // Bounded condition ranges apply to scale triggers only; number triggers
    // accept any range. Missing or non-numeric scale bounds skip the check.
    scaleBounds(index) {
      if (this.selectedTrigger(index)?.type !== ITEM_TYPES.SCALE) {
        return null
      }

      const min = this.selectedTrigger(index)?.min
      const max = this.selectedTrigger(index)?.max

      if (!_.isNumber(min) || !_.isNumber(max)) {
        return null
      }

      return { min, max }
    },

    parseBound(value) {
      if (_.isNumber(value)) {
        return value
      }

      if (!_.isString(value) || _.isEmpty(value.trim())) {
        return null
      }

      const parsed = Number(value)
      return _.isFinite(parsed) ? parsed : null
    },

    // Untouched drafts (null) converge on the saved rule; an explicit edit
    // that parses to nothing holds the emission instead of reverting.
    resolveRangeBound(draftBound, savedBound) {
      return draftBound !== null ? this.parseBound(draftBound) : savedBound
    },

    resolvedRange(index) {
      const draft = this.blockDraft(index)
      const savedRange = draft.triggerItemId ? {} : this.savedRule(index)?.range

      return {
        min: this.resolveRangeBound(draft.min ?? null, savedRange?.min),
        max: this.resolveRangeBound(draft.max ?? null, savedRange?.max),
      }
    },

    // Returns the message-worthy range violation, or null while the range is
    // incomplete or valid. Bounds are checked first; the duplicate check only
    // applies to complete, in-bounds ranges.
    getRangeError(index) {
      return (
        this.getRangeBoundsError(index) ?? this.getDuplicateRangeError(index)
      )
    },

    getRangeBoundsError(index) {
      const { min, max } = this.resolvedRange(index)

      if (!_.isNumber(min) || !_.isNumber(max)) {
        return null
      }

      if (max < min) {
        return this.$t('checklist.scale.error')
      }

      const bounds = this.scaleBounds(index)

      if (bounds && (min < bounds.min || max > bounds.max)) {
        return this.$t(
          'checklist.visibility_rule.condition_range_out_of_bounds',
          bounds
        )
      }

      return null
    },

    // Draft-aware (trigger, min, max) of a block; light on purpose — the
    // duplicate check must not re-enter the full rule resolution.
    getRangeTuple(index) {
      if (this.conditionKind(index) !== CONDITION_TYPES.RANGE) {
        return null
      }

      const { min, max } = this.resolvedRange(index)

      if (!_.isNumber(min) || !_.isNumber(max)) {
        return null
      }

      return {
        triggerItemId: utils.getEntityId(this.selectedTriggerItemId(index)),
        min,
        max,
      }
    },

    getRuleRangeTuple(rule) {
      const min = rule?.range?.min
      const max = rule?.range?.max

      if (!_.isNumber(min) || !_.isNumber(max)) {
        return null
      }

      return {
        triggerItemId: utils.getEntityId(rule?.triggerItemId),
        min,
        max,
      }
    },

    // A block may not reuse the exact (trigger, min, max) of another block.
    // Only the "mover" — whose draft rule differs from its own saved rule —
    // is flagged, and only when the accepted set demoted it because a
    // peer's accepted tuple matched; untouched and legacy-duplicate blocks
    // never error.
    getDuplicateRangeError(index) {
      const tuple = this.getRangeTuple(index)

      if (!tuple) {
        return null
      }

      const savedTuple = this.savedRule(index)
        ? this.getRuleRangeTuple(this.savedRule(index))
        : null

      if (savedTuple && _.isEqual(tuple, savedTuple)) {
        return null
      }

      const draftRule = {
        triggerItemId: tuple.triggerItemId,
        range: { min: tuple.min, max: tuple.max },
      }
      const acceptedRule = this.getAcceptedBlockRules()[index]

      return _.isEqual(acceptedRule, draftRule)
        ? null
        : this.$t('checklist.visibility_rule.condition_range_duplicated')
    },

    hasRangeError(index) {
      return !_.isEmpty(this.getRangeError(index))
    },

    rangeError(index) {
      const error = this.getRangeError(index)

      return error ? [error] : []
    },

    handleTriggerChange(index, triggerItemId) {
      const draft = this.ensureDraft(index)
      draft.triggerItemId = triggerItemId ?? null
      draft.optionId = null
      draft.optionIds = []
      draft.multiTouched = false
      draft.min = null
      draft.max = null
      this.emitRulesIfChanged()
    },

    handleOptionChange(index, optionId) {
      const draft = this.ensureDraft(index)
      draft.optionId = optionId ?? null
      this.emitRulesIfChanged()
    },

    handleMultiChange(index, optionIds) {
      const draft = this.ensureDraft(index)
      draft.optionIds = _.isArray(optionIds) ? [...optionIds] : []
      draft.multiTouched = true
      this.emitRulesIfChanged()
    },

    handleMinInput(index, value) {
      const draft = this.ensureDraft(index)
      draft.min = value
      this.emitRulesIfChanged()
    },

    handleMaxInput(index, value) {
      const draft = this.ensureDraft(index)
      draft.max = value
      this.emitRulesIfChanged()
    },

    handleAddRule() {
      if (!this.canAddRule) {
        return
      }

      this.drafts.push(this.buildEmptyDraft())
    },

    handleRemoveRule(index) {
      this.drafts.splice(index, 1)

      if (this.isPendingBlock(index)) {
        this.discardEditorIfEmptied()
        return
      }

      const remainingRules = [...this.savedRules]
      remainingRules.splice(index, 1)

      if (_.isEmpty(this.drafts)) {
        this.lastEmittedRules = []
        this.$emit('remove')
        return
      }

      this.lastEmittedRules = _.cloneDeep(remainingRules)
      // The parent must not discard the editor while a pending block
      // survives the removal, so the change reports that explicitly.
      this.$emit('change', {
        visibilityRules: remainingRules,
        hasPendingDraft: this.drafts.length > remainingRules.length,
      })
    },

    isPendingBlock(index) {
      return index >= this.savedRules.length
    },

    // A pending-only discard on an unconditioned item leaves the editor
    // without blocks: it closes instead of lingering empty.
    discardEditorIfEmptied() {
      if (!_.isEmpty(this.drafts) || !_.isEmpty(this.savedRules)) {
        return
      }

      this.$emit('discard')
    },

    // After a save attempt the payload is authoritative: drafts converge on
    // the saved rules, dropping unemitted pending blocks and held edits.
    resetDraftsToSavedRules() {
      this.drafts = this.savedRules.map(() => null)
      this.lastEmittedRules = null
      this.seedFirstRuleBlock()
    },

    // Resolves a block to its draft-or-saved fallback rule: draft values
    // converge on the saved rule per field, so an incomplete block keeps
    // the saved rule and a pending block is skipped. Duplicate checks are
    // NOT applied here — `getAcceptedBlockRules` owns them.
    resolveBlockRuleRaw(index) {
      const savedRule = this.savedRule(index)
      const triggerItemId = this.selectedTriggerItemId(index)

      if (!triggerItemId) {
        return savedRule
      }

      const kind = this.conditionKind(index)

      if (kind === CONDITION_TYPES.RANGE) {
        const { min, max } = this.resolvedRange(index)

        if (
          !_.isNumber(min) ||
          !_.isNumber(max) ||
          this.getRangeBoundsError(index)
        ) {
          return savedRule
        }

        return { triggerItemId, range: { min, max } }
      }

      if (kind === CONDITION_TYPES.MULTI) {
        const optionIds = this.selectedMultiOptionIds(index)

        if (_.isEmpty(optionIds)) {
          return savedRule
        }

        return { triggerItemId, optionIds: [...optionIds] }
      }

      const optionId = this.selectedOptionId(index)

      if (!optionId) {
        return savedRule
      }

      return { triggerItemId, optionIds: [optionId] }
    },

    // Resolves every block to its accepted rule: the draft-derived rule
    // when complete, valid and not duplicating an accepted peer, else the
    // saved rule. Demotion repeats until stable so a peer blocked by a
    // third block contributes its accepted fallback, not its rejected
    // draft. Mover-only: untouched blocks keep their saved rules even when
    // those duplicate each other (legacy data).
    getAcceptedBlockRules() {
      const accepted = this.drafts.map((draft, index) =>
        this.resolveBlockRuleRaw(index)
      )

      let changed = true

      while (changed) {
        changed = false

        accepted.forEach((rule, index) => {
          const savedRule = this.savedRule(index)

          if (!rule || !rule.range || rule === savedRule) {
            return
          }

          const tuple = this.getRuleRangeTuple(rule)
          const isDuplicated = accepted.some((peerRule, peerIndex) => {
            if (peerIndex === index || !peerRule) {
              return false
            }

            const peerTuple = this.getRuleRangeTuple(peerRule)

            return peerTuple && _.isEqual(peerTuple, tuple)
          })

          if (isDuplicated) {
            accepted[index] = savedRule
            changed = true
          }
        })
      }

      return accepted
    },

    emitRulesIfChanged() {
      const rules = this.getAcceptedBlockRules().filter(Boolean)

      if (_.isEqual(rules, this.savedRules)) {
        return
      }

      this.lastEmittedRules = _.cloneDeep(rules)
      this.$emit('change', { visibilityRules: rules })
    },
  },
}
</script>

<style lang="scss" scoped>
.conditional-editor {
  border: 1px solid $color-blue-grey-lighten1;

  &__remove-column {
    flex: 0 0 48px;
    margin-inline-end: -17px;
  }

  & ::v-deep [data-testid^='conditionalEditor-button-removeRule'] .v-icon {
    font-size: 20px !important;
  }

  & ::v-deep .v-btn.custom-button.v-btn--disabled.v-btn--has-bg {
    background: $color-blue-grey !important;
    opacity: 0.4;

    span.button-text,
    i {
      color: $color-white !important;
    }
  }

  & ::v-deep .v-text-field--outlined {
    input {
      color: $color-blue-grey-darken3;
      font-weight: 400;
    }

    .v-text-field__prefix {
      color: $color-blue-grey-lighten1;
    }

    // The `.white` utility paints the whole component root, including the
    // error-messages zone below the field; keep the white on the field box
    // only so the editor card color shows through the messages area.
    &.white {
      background-color: transparent !important;
    }

    .v-input__slot {
      background-color: $color-white;
    }
  }
}
</style>
